import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  getAutoScreenFeatureStatus,
  getAutoScreenSettings,
  updateAutoScreenSettings,
  triggerAutoScreenRunNow,
  getAutoScreenRuns,
  AutoScreenSettings,
  AutoScreenSettingsPatch,
  AutoScreenRun,
  AutoScreenRunNowResponse,
} from '../../services/api';

// Narrowed shape extracted from rejected thunk errors. Accepts both real
// AxiosError instances and the plain `{ response, message }` reject shape used
// in tests. Falls back to `axios.isAxiosError` only when available, since the
// test harness mocks axios without that helper.
interface HttpErrorLike {
  response?: { status?: number; data?: { error?: string; feature_disabled?: boolean } };
  message?: string;
}

const asHttpError = (err: unknown): HttpErrorLike | null => {
  if (typeof axios?.isAxiosError === 'function' && axios.isAxiosError(err)) {
    return err as HttpErrorLike;
  }
  if (err && typeof err === 'object' && ('response' in err || 'message' in err)) {
    return err as HttpErrorLike;
  }
  return null;
};

export type AutoScreenFetchStatus = 'idle' | 'loading' | 'failed';
export type AutoScreenSaveStatus = 'idle' | 'saving' | 'failed';
export type AutoScreenRunNowStatus = 'idle' | 'queueing' | 'failed';

export interface AutoScreenState {
  // Feature flag - null until first fetch resolves. False/true = settled.
  featureEnabled: boolean | null;
  settings: AutoScreenSettings | null;
  recentRuns: AutoScreenRun[];
  status: {
    fetch: AutoScreenFetchStatus;
    save: AutoScreenSaveStatus;
    runNow: AutoScreenRunNowStatus;
  };
  errors: {
    fetch?: string;
    runs?: string;
    save?: string;
    runNow?: string;
  };
  // Last 402 paid-tier signal - UI gates upgrade prompt on this.
  paidTierRequired: boolean;
  // Last 409 schedule_not_configured signal - UI shows "Configure & enable first" toast.
  scheduleNotConfigured: boolean;
}

const initialState: AutoScreenState = {
  featureEnabled: null,
  settings: null,
  recentRuns: [],
  status: { fetch: 'idle', save: 'idle', runNow: 'idle' },
  errors: {},
  paidTierRequired: false,
  scheduleNotConfigured: false,
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchFeatureStatus = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>('autoScreen/fetchFeatureStatus', async (_arg, { rejectWithValue }) => {
  try {
    const res = await getAutoScreenFeatureStatus();
    return Boolean(res?.enabled);
  } catch (err: unknown) {
    // 503 with feature_disabled is also a "feature off" signal - surface as false, not error.
    const httpErr = asHttpError(err);
    if (httpErr) {
      if (httpErr.response?.status === 503 && httpErr.response?.data?.feature_disabled) {
        return false;
      }
      return rejectWithValue(httpErr.message || 'Failed to fetch feature status');
    }
    return rejectWithValue(
      err instanceof Error ? err.message : 'Failed to fetch feature status'
    );
  }
});

export const fetchSettings = createAsyncThunk<
  AutoScreenSettings,
  void,
  { rejectValue: { message: string; featureDisabled?: boolean } }
>('autoScreen/fetchSettings', async (_arg, { rejectWithValue }) => {
  try {
    return await getAutoScreenSettings();
  } catch (err: unknown) {
    const httpErr = asHttpError(err);
    if (httpErr) {
      if (httpErr.response?.status === 503 && httpErr.response?.data?.feature_disabled) {
        return rejectWithValue({ message: 'feature_disabled', featureDisabled: true });
      }
      return rejectWithValue({ message: httpErr.message || 'Failed to fetch settings' });
    }
    return rejectWithValue({
      message: err instanceof Error ? err.message : 'Failed to fetch settings',
    });
  }
});

export const saveSettings = createAsyncThunk<
  AutoScreenSettings,
  AutoScreenSettingsPatch,
  { rejectValue: { message: string; paidTierRequired?: boolean; featureDisabled?: boolean } }
>('autoScreen/saveSettings', async (patch, { rejectWithValue }) => {
  try {
    return await updateAutoScreenSettings(patch);
  } catch (err: unknown) {
    const httpErr = asHttpError(err);
    if (httpErr) {
      const status = httpErr.response?.status;
      const data = httpErr.response?.data;
      if (status === 402 && data?.error === 'paid_tier_required') {
        return rejectWithValue({ message: 'paid_tier_required', paidTierRequired: true });
      }
      if (status === 503 && data?.feature_disabled) {
        return rejectWithValue({ message: 'feature_disabled', featureDisabled: true });
      }
      return rejectWithValue({ message: httpErr.message || 'Failed to save settings' });
    }
    return rejectWithValue({
      message: err instanceof Error ? err.message : 'Failed to save settings',
    });
  }
});

export const triggerRunNow = createAsyncThunk<
  AutoScreenRunNowResponse,
  void,
  {
    rejectValue: {
      message: string;
      paidTierRequired?: boolean;
      scheduleNotConfigured?: boolean;
      featureDisabled?: boolean;
    };
  }
>('autoScreen/triggerRunNow', async (_arg, { rejectWithValue }) => {
  try {
    return await triggerAutoScreenRunNow();
  } catch (err: unknown) {
    const httpErr = asHttpError(err);
    if (httpErr) {
      const status = httpErr.response?.status;
      const data = httpErr.response?.data;
      if (status === 402 && data?.error === 'paid_tier_required') {
        return rejectWithValue({ message: 'paid_tier_required', paidTierRequired: true });
      }
      if (status === 409 && data?.error === 'schedule_not_configured') {
        return rejectWithValue({
          message: 'schedule_not_configured',
          scheduleNotConfigured: true,
        });
      }
      if (status === 503 && data?.feature_disabled) {
        return rejectWithValue({ message: 'feature_disabled', featureDisabled: true });
      }
      return rejectWithValue({ message: httpErr.message || 'Failed to queue run' });
    }
    return rejectWithValue({
      message: err instanceof Error ? err.message : 'Failed to queue run',
    });
  }
});

export const fetchRecentRuns = createAsyncThunk<
  AutoScreenRun[],
  number | undefined,
  { rejectValue: string }
>('autoScreen/fetchRecentRuns', async (limit, { rejectWithValue }) => {
  try {
    return await getAutoScreenRuns(limit ?? 20);
  } catch (err: unknown) {
    const httpErr = asHttpError(err);
    if (httpErr) {
      return rejectWithValue(httpErr.message || 'Failed to fetch runs');
    }
    return rejectWithValue(err instanceof Error ? err.message : 'Failed to fetch runs');
  }
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const autoScreenSlice = createSlice({
  name: 'autoScreen',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.errors = {};
    },
    clearPaidTierFlag: (state) => {
      state.paidTierRequired = false;
    },
    clearScheduleNotConfiguredFlag: (state) => {
      state.scheduleNotConfigured = false;
    },
    // Used when a 503 from any settings/runs endpoint should hide the card.
    setFeatureDisabled: (state) => {
      state.featureEnabled = false;
    },
    resetAutoScreenState: () => initialState,
    // Optimistic local toggle for snappy UX before server confirms.
    setSettingsLocal: (state, action: PayloadAction<Partial<AutoScreenSettings>>) => {
      if (state.settings) {
        state.settings = { ...state.settings, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------------- featureStatus ----------------
      .addCase(fetchFeatureStatus.pending, (state) => {
        state.status.fetch = 'loading';
        delete state.errors.fetch;
      })
      .addCase(fetchFeatureStatus.fulfilled, (state, action) => {
        state.status.fetch = 'idle';
        state.featureEnabled = action.payload;
      })
      .addCase(fetchFeatureStatus.rejected, (state, action) => {
        // Treat fetch failures as "unknown / off" - kill switch defaults closed.
        state.status.fetch = 'failed';
        state.featureEnabled = false;
        const msg = action.payload || action.error.message;
        if (msg) state.errors.fetch = msg;
      })
      // ---------------- fetchSettings ----------------
      .addCase(fetchSettings.pending, (state) => {
        state.status.fetch = 'loading';
        delete state.errors.fetch;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.status.fetch = 'idle';
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.status.fetch = 'failed';
        if (action.payload?.featureDisabled) {
          state.featureEnabled = false;
        }
        const msg = action.payload?.message || action.error.message;
        if (msg) state.errors.fetch = msg;
      })
      // ---------------- saveSettings ----------------
      .addCase(saveSettings.pending, (state) => {
        state.status.save = 'saving';
        delete state.errors.save;
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.status.save = 'idle';
        state.settings = action.payload;
        state.paidTierRequired = false;
      })
      .addCase(saveSettings.rejected, (state, action) => {
        state.status.save = 'failed';
        if (action.payload?.featureDisabled) {
          state.featureEnabled = false;
        }
        if (action.payload?.paidTierRequired) {
          state.paidTierRequired = true;
        }
        const msg = action.payload?.message || action.error.message;
        if (msg) state.errors.save = msg;
      })
      // ---------------- triggerRunNow ----------------
      .addCase(triggerRunNow.pending, (state) => {
        state.status.runNow = 'queueing';
        delete state.errors.runNow;
        state.scheduleNotConfigured = false;
      })
      .addCase(triggerRunNow.fulfilled, (state, action) => {
        state.status.runNow = 'idle';
        // Mark schedule row as having a pending request so UI can disable Run-now CTA.
        if (state.settings && action.payload.queued) {
          state.settings.runNowRequestedAt = new Date().toISOString();
        }
      })
      .addCase(triggerRunNow.rejected, (state, action) => {
        state.status.runNow = 'failed';
        if (action.payload?.featureDisabled) {
          state.featureEnabled = false;
        }
        if (action.payload?.paidTierRequired) {
          state.paidTierRequired = true;
        }
        if (action.payload?.scheduleNotConfigured) {
          state.scheduleNotConfigured = true;
        }
        const msg = action.payload?.message || action.error.message;
        if (msg) state.errors.runNow = msg;
      })
      // ---------------- fetchRecentRuns ----------------
      .addCase(fetchRecentRuns.fulfilled, (state, action) => {
        state.recentRuns = action.payload;
        // If most recent run is no longer 'running' AND row says no pending request,
        // clear any stale runNowRequestedAt the server has since cleared.
        const latest = action.payload[0];
        if (latest && state.settings) {
          const stillRunning = latest.status === 'queued' || latest.status === 'running';
          if (!stillRunning) {
            // The orchestrator clears run_now_requested_at on pickup, but the
            // local flag must also be cleared so the Run-now CTA re-enables
            // even if the user navigated away before /settings repolled.
            state.settings.runNowRequestedAt = null;
          }
        }
      })
      .addCase(fetchRecentRuns.rejected, (state, action) => {
        // Use a separate error key so a flaky /runs poll does not stomp the
        // settings-fetch error that drives the card's load-failure banner.
        const msg = action.payload || action.error.message;
        if (msg) state.errors.runs = msg;
      });
  },
});

export const {
  clearErrors,
  clearPaidTierFlag,
  clearScheduleNotConfiguredFlag,
  setFeatureDisabled,
  resetAutoScreenState,
  setSettingsLocal,
} = autoScreenSlice.actions;

export default autoScreenSlice.reducer;
